import SearchIcon from '@mui/icons-material/Search';
import {
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { listInvoiceGroupsApi } from 'api/invoiceGroup';

const STATUS_FILTER = {
  ALL: 'ALL',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
};

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

const InvoiceGroups = () => {
  const theme = useTheme();
  const [invoiceGroups, setInvoiceGroups] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER.ALL);
  const [orderBy, setOrderBy] = useState('invoiceGroupName');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    document.title = 'Invoice Groups - SmartOS';
  }, []);

  useEffect(() => {
    const fetchInvoiceGroups = async () => {
      setIsLoading(true);
      setIsError(false);

      try {
        const response = await listInvoiceGroupsApi();
        setInvoiceGroups(response?.results ?? []);
        setTotalCount(response?.count ?? 0);
      } catch (error) {
        setIsError(true);
        setInvoiceGroups([]);
        setTotalCount(0);
        enqueueSnackbar('Failed to load invoice groups', { variant: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoiceGroups();
  }, []);

  // Reset to the first page whenever the filtered/sorted result set changes.
  useEffect(() => {
    setPage(0);
  }, [searchText, statusFilter, order, orderBy]);

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const displayedGroups = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    const filtered = invoiceGroups.filter((group) => {
      const matchesSearch = !query || group.invoiceGroupName?.toLowerCase().includes(query);
      const matchesStatus =
        statusFilter === STATUS_FILTER.ALL ||
        (statusFilter === STATUS_FILTER.ACTIVE && group.isActive) ||
        (statusFilter === STATUS_FILTER.INACTIVE && !group.isActive);

      return matchesSearch && matchesStatus;
    });

    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      if (orderBy === 'status') {
        comparison = Number(a.isActive) - Number(b.isActive);
      } else {
        comparison = (a.invoiceGroupName || '').localeCompare(b.invoiceGroupName || '');
      }

      return order === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [invoiceGroups, searchText, statusFilter, order, orderBy]);

  const paginatedGroups = useMemo(
    () => displayedGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [displayedGroups, page, rowsPerPage]
  );

  return (
    <Grid container direction='column' sx={{ px: 3 }} gap={2}>
      <Grid item container justifyContent='space-between' alignItems='center' rowGap={2}>
        <Grid item>
          <Typography component='h1' variant='h5'>
            Invoice Groups
          </Typography>
        </Grid>
        <Grid item>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='center'>
            <TextField
              size='small'
              placeholder='Search by name'
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon fontSize='small' />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size='small' sx={{ minWidth: 160 }}>
              <InputLabel id='invoice-group-status-filter-label'>Status</InputLabel>
              <Select
                labelId='invoice-group-status-filter-label'
                label='Status'
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <MenuItem value={STATUS_FILTER.ALL}>All</MenuItem>
                <MenuItem value={STATUS_FILTER.ACTIVE}>Active</MenuItem>
                <MenuItem value={STATUS_FILTER.INACTIVE}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      </Grid>

      {isLoading ? (
        <Grid item container justifyContent='center' sx={{ py: 6 }}>
          <CircularProgress />
        </Grid>
      ) : (
        <Grid item>
          <TableContainer component={Paper}>
            <Table aria-label='invoice groups table'>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={orderBy === 'invoiceGroupName' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'invoiceGroupName'}
                      direction={orderBy === 'invoiceGroupName' ? order : 'asc'}
                      onClick={() => handleSort('invoiceGroupName')}
                    >
                      Invoice Group Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align='right' sortDirection={orderBy === 'status' ? order : false}>
                    <TableSortLabel
                      active={orderBy === 'status'}
                      direction={orderBy === 'status' ? order : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align='center'>
                      {isError ? 'Unable to load invoice groups.' : 'No invoice groups found.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedGroups.map((group) => (
                    <TableRow key={group.invoiceGroupId} hover>
                      <TableCell>
                        <Link
                          to={`/adminCenter/invoiceGroups/${group.invoiceGroupId}`}
                          state={{
                            invoiceGroupName: group.invoiceGroupName,
                            billTo: group.billTo,
                          }}
                          style={{ color: theme.palette.text.primary }}
                        >
                          {group.invoiceGroupName}
                        </Link>
                      </TableCell>
                      <TableCell align='right'>
                        <Chip
                          size='small'
                          label={group.isActive ? 'Active' : 'Inactive'}
                          color={group.isActive ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Grid
            container
            justifyContent='space-between'
            alignItems='center'
            sx={{ mt: 1, flexWrap: 'wrap' }}
          >
            <Typography variant='body2' color='text.secondary'>
              {`Showing ${displayedGroups.length} of ${totalCount} invoice groups`}
            </Typography>
            <TablePagination
              component='div'
              count={displayedGroups.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default InvoiceGroups;
